<template>
  <StepCard title="Detalhes da Solicitação e Despesas Médicas">
    <v-row dense>

      <v-col cols="12" sm="4">
        <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template v-slot:activator="{ props }">
            <v-text-field label="Data da Despesa" :model-value="formattedExpenseDate" readonly variant="outlined"
              density="compact" v-bind="props" placeholder="dd/mm/aaaa"
              :error-messages="formErrors.expenseDate"></v-text-field>
          </template>
          <v-date-picker v-model="expenseDateObject" @update:modelValue="dateMenu = false" locale="pt-BR" title=""
            hide-header></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field label="Valor Total" v-model="formattedTotalValue" prefix="R$" variant="outlined" density="compact"
          :error-messages="formErrors.totalValue"></v-text-field>
      </v-col>
      <v-col cols="12" sm="4">
        <v-select label="Tipo de Serviço" :items="reimbursementTypes" item-title="descricao" item-value="id"
          v-model="formData.serviceType" variant="outlined" density="compact" :loading="reimbursementTypesLoading"
          :error-messages="formErrors.serviceType"></v-select>
      </v-col>
      <v-col cols="12">
        <v-text-field label="Número do Documento Fiscal (Nota/Recibo)" v-model="formData.invoiceNumber"
          variant="outlined" density="compact" :error-messages="formErrors.invoiceNumber"></v-text-field>
      </v-col>
    </v-row>
    <v-row dense class="mt-2">
      <v-col cols="12" md="6">
        <v-text-field label="CPF/CNPJ do Prestador" v-model="formattedCpfCnpj" variant="outlined" density="compact"
          :error-messages="formErrors.providerCpfCnpj"></v-text-field>
      </v-col>
      <v-col cols="12" md="6" v-if="formData.providerName">
        <v-text-field label="Nome do Prestador" disabled v-model="formData.providerName" variant="outlined"
          density="compact" :error-messages="formErrors.providerName" :loading="prestadorLoading"></v-text-field>
      </v-col>
      <v-col cols="12" md="6" v-if="!formData.providerName">
        <v-text-field label="Nome do Prestador" v-model="formData.providerNameManual" variant="outlined"
          density="compact" :error-messages="formErrors.providerNameManual" :loading="prestadorLoading"></v-text-field>
      </v-col>
    </v-row>
  </StepCard>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { gql } from '@apollo/client';
import StepCard from './StepCard.vue';

// O v-model do pai será mapeado para esta ref 'formData'.
// Qualquer alteração aqui será refletida no pai.
const formData = defineModel({
    type: Object,
    required: true,
});

defineProps({
    formErrors: {
        type: Object,
        required: true,
    },
    reimbursementTypes: {
        type: Array,
        required: true,
    },
    reimbursementTypesLoading: {
        type: Boolean,
        required: true,
    }
});

const dateMenu = ref(false);

// GraphQL Queries
const GET_PROVIDER_BY_CNPJ_QUERY = gql`
  query GetProviderByCnpj($cnpj: String!) {
    prestadoresPorCnpj(cnpj: $cnpj) { nome }
  }
`;

// Apollo Hooks
const { result: prestadoresResult, loading: prestadorLoading } = useQuery(
    GET_PROVIDER_BY_CNPJ_QUERY,
    computed(() => ({ cnpj: formData.value.providerCpfCnpj })),
    {
        enabled: computed(() => {
            const cleanedCpfCnpj = (formData.value.providerCpfCnpj || '').replace(/\D/g, '');
            return cleanedCpfCnpj.length === 11 || cleanedCpfCnpj.length === 14;
        }),
    }
);

// Watchers
watch(prestadoresResult, (newResult) => {
    if (newResult && newResult.prestadoresPorCnpj && newResult.prestadoresPorCnpj.length > 0) {
        formData.value.providerName = newResult.prestadoresPorCnpj[0].nome;
        formData.value.providerNameManual = '';
    }
});

watch(() => formData.value.providerCpfCnpj, () => {
    formData.value.providerName = '';
});

// Computed Properties para formatação
const formattedExpenseDate = computed(() => {
    if (!formData.value.expenseDate) return null;
    const [year, month, day] = formData.value.expenseDate.split('-');
    return `${day}/${month}/${year}`;
});



const expenseDateObject = computed({
    get: () => formData.value.expenseDate ? new Date(formData.value.expenseDate + 'T00:00:00') : null,
    set: (val) => {
        formData.value.expenseDate = val ? val.toISOString().split('T')[0] : null;
    }
});

const formattedTotalValue = computed({
    get() {
        if (formData.value.totalValue === null || formData.value.totalValue === undefined) return '';
        return formData.value.totalValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    },
    set(newValue) {
        if (!newValue) {
            formData.value.totalValue = null;
            return;
        }
        const digits = String(newValue).replace(/\D/g, '');
        if (digits) {
            formData.value.totalValue = parseFloat(digits) / 100;
        }
    },
});

const formattedCpfCnpj = computed({
    get() {
        const cleaned = (formData.value.providerCpfCnpj || '').replace(/\D/g, '');
        if (cleaned.length <= 11) {
            return cleaned
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        }
        return cleaned
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
            .slice(0, 18);
    },
    set(newValue) {
        formData.value.providerCpfCnpj = (newValue || '').replace(/\D/g, '').slice(0, 14);
    }
});
</script>
